import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthenticateserviceService } from '../_service/authenticateservice.service';
import { GuestTokenService } from '../_service/guest-token.service';
import { getAppBasePath } from '../utils/share-link.utils';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthenticateserviceService,
    private guestTokenService: GuestTokenService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Observable<boolean> {
    // Real user token exists — allow immediately
    if (localStorage.getItem('token')) {
      return true;
    }

    // Logged-in user whose short access token lapsed: restore the REAL session
    // from the refreshToken (30-day idToken) — NOT a guest token. This is the
    // missing path that let the guest token hijack a registered session.
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined') {
      return this.authService.toqqerUserLogin(refreshToken).pipe(
        map(() => {
          this.authService.isAuthenticatedSubject.next(true);
          return true;
        }),
        catchError(() => {
          // refreshToken genuinely dead → full logout (server + local).
          this.authService.logout();
          return of(false);
        })
      );
    }

    // Guest token already exists — allow (truly anonymous visitor)
    if (localStorage.getItem('guestToken')) {
      this.authService.isAuthenticatedSubject.next(true);
      return true;
    }

    // No tokens at all — try to acquire a guest token
    return this.guestTokenService.requestGuestToken().pipe(
      map(token => {
        this.authService.isAuthenticatedSubject.next(true);
        return true;
      }),
      catchError(error => {
        // Guest token failed (409 = already used, or network error)
        const isGuestExpired = error?.status === 409;
        this.redirectToLogin(isGuestExpired);
        return of(false);
      })
    );
  }

  private redirectToLogin(guestExpired: boolean = false): void {
    this.authService.isAuthenticatedSubject.next(false);
    let redirectUrl = window.location.pathname + window.location.search;
    const basePath = getAppBasePath();
    if (basePath && redirectUrl.startsWith(basePath)) {
      redirectUrl = redirectUrl.substring(basePath.length);
    }
    if (!redirectUrl.startsWith('/')) {
      redirectUrl = '/' + redirectUrl;
    }
    // Never save /login (with or without query params like ?guestExpired=true).
    // If AuthGuard fires while user is already on the login page, saving the
    // current URL would overwrite the original target and trap the user on
    // /login after a successful sign-in.
    if (redirectUrl !== '/' && !redirectUrl.startsWith('/login')) {
      localStorage.setItem('redirectAfterLogin', redirectUrl);
    }
    const queryParams = guestExpired ? { guestExpired: 'true' } : {};
    this.router.navigate(['/login'], { replaceUrl: true, queryParams });
  }
}
